import { Link } from "@tanstack/react-router";
import { BookHeart, ClipboardList, Loader2, RotateCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { MedicoPipelineItem } from "@/api/db/medico";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { useMedicoRetryStage, useMedicoUnlink } from "@/hooks/use-medico";
import { MedicoLabels } from "@/lib/labels/medico";

const QUIET_LINK =
	"inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

export function PipelineActions({
	item,
	openableManualId,
}: {
	item: MedicoPipelineItem;
	openableManualId: MedicoPipelineItem["medManualId"];
}) {
	const unlink = useMedicoUnlink();
	const retry = useMedicoRetryStage();

	return (
		<div className="flex flex-wrap items-center gap-3">
			{item.stage === "error" && (
				<Button
					type="button"
					variant="default"
					size="sm"
					className="px-4"
					disabled={retry.isPending}
					data-slot="retry-stage"
					onClick={() => retry.mutate({ groupId: item.groupId })}
				>
					{retry.isPending && <Loader2 className="animate-spin" />}
					{!retry.isPending && <RotateCcw />}
					{retry.isPending && MedicoLabels.jobRetrying}
					{!retry.isPending && MedicoLabels.jobRetry}
				</Button>
			)}

			{openableManualId !== null && (
				<Link
					to="/clinica/manual/$id"
					params={{ id: openableManualId }}
					data-slot="open-manual"
					className={QUIET_LINK}
				>
					<BookHeart className="size-3" />
					{MedicoLabels.pipeline.openManual}
				</Link>
			)}

			{item.personId !== null && (
				<Link
					to="/pessoa/$id"
					params={{ id: item.personId }}
					search={{ tab: "cadastro", focus: "clinico" }}
					data-slot="open-prontuario"
					className={QUIET_LINK}
				>
					<ClipboardList className="size-3" />
					{MedicoLabels.pipeline.openProntuario}
				</Link>
			)}

			<span className="flex-1" />

			<ConfirmationDialog
				slot="unlink-trigger"
				tone="destructive"
				title={MedicoLabels.pipeline.unlinkConfirm({
					partsCount: item.partsCount,
					revoked: item.stage === "revoked",
				})}
				pendingLabel={MedicoLabels.pipeline.unlinking}
				isPending={unlink.isPending}
				onConfirm={() => unlink.mutate({ transcriptId: item.transcriptId })}
				className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
			>
				{MedicoLabels.pipeline.unlink}
			</ConfirmationDialog>
		</div>
	);
}
